import { useCallback, useState } from 'react';
import {
  Colors,
  Flex,
  IconChevronDown,
  IconChevronRight,
  IconChevronUp,
  RemoteImage,
  Typography,
} from '@banhala/ui-kit';
import type {
  ExpandableCategoryListProps,
  ExpandableCategoryEntity,
} from './types';
import { bridge, isNotNil } from '../../../../utils';
import { useOpenDeeplink } from '../../../../hooks';
import type { IScreensLogging } from '../../../../api/types/IScreenLogging';

export const ExpandableCategoryList = ({
  entity,
  body,
  logging,
}: ExpandableCategoryListProps) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const handleToggle = useCallback(() => {
    setIsExpanded(prev => !prev);
  }, []);

  if (!entity || entity.length === 0) return null;

  return (
    <Flex>
      {/* 헤더 */}
      <Flex.Row
        alignItems="center"
        onPress={handleToggle}
        padding={{ horizontal: 16, vertical: 16 }}
      >
        {isNotNil(body?.item.thumbnail) && (
          <Flex margin={{ right: 8 }}>
            <RemoteImage
              uri={body!.item.thumbnail}
              width={28}
              height={28}
              resizeMode="cover"
            />
          </Flex>
        )}
        <Flex flex={1}>
          <Typography type="subtitle1" color="content_primary">
            {body?.item.name ?? ''}
          </Typography>
        </Flex>
        {isExpanded ? (
          <IconChevronUp size={20} fill={Colors.content_primary} />
        ) : (
          <IconChevronDown size={20} fill={Colors.content_primary} />
        )}
      </Flex.Row>

      {/* 카테고리 리스트 */}
      {isExpanded && (
        <Flex>
          <Flex borderTopWidth={1} borderColor="border_tertiary" />
          <Flex padding={{ horizontal: 16 }}>
            {pairItems(entity).map((pair, rowIndex, arr) => (
              <Flex.Row key={rowIndex}>
                {pair.map(item => (
                  <CategoryItem
                    key={item.item.name}
                    entity={item}
                    logging={logging}
                    showDivider={rowIndex < arr.length - 1}
                  />
                ))}
                {pair.length === 1 && <Flex flex={1} />}
              </Flex.Row>
            ))}
          </Flex>
        </Flex>
      )}
    </Flex>
  );
};

const CategoryItem = ({
  entity,
  logging,
  showDivider,
}: {
  entity: ExpandableCategoryEntity;
  logging: IScreensLogging;
  showDivider: boolean;
}) => {
  const { openDeeplink } = useOpenDeeplink();

  const handlePress = useCallback(() => {
    bridge.logAnalyticsEvent({
      name: 'CLICK_EXPANDABLE_CATEGORY_LIST',
      analytics: { ...logging?.analytics, ...entity.logging?.analytics },
      ably_only_log: { ...logging?.ably_only_log, ...entity.logging?.ably_only_log },
    });
    openDeeplink(entity.item.deeplink);
  }, [entity, logging, openDeeplink]);

  return (
    <Flex
      flex={1}
      onPress={handlePress}
      borderBottomWidth={showDivider ? 1 : 0}
      borderColor="border_tertiary"
    >
      <Flex.Row
        alignItems="center"
        padding={{ horizontal: 8, vertical: 14 }}
      >
        <Flex flex={1}>
          <Typography type="body2" color="content_primary">
            {entity.item.name}
          </Typography>
        </Flex>
        <IconChevronRight size={16} fill={Colors.content_secondary} />
      </Flex.Row>
    </Flex>
  );
};

const pairItems = <T,>(items: T[]): T[][] => {
  const pairs: T[][] = [];
  for (let i = 0; i < items.length; i += 2) {
    pairs.push(items.slice(i, i + 2));
  }
  return pairs;
};
